import type { StructureType } from './structures.ts';
import type { GearBaseId } from './gear.ts';

/**
 * Saberes (docs/SABERES.md): what the old garrison knew and lost, found now and then in the hard places and learnt by
 * the whole bastion. Terraced land only. Every number here is a starting one, to be checked with `balance:saberes`.
 */

/** What a saber opens: a structure, another way to build a tower, a rite at the keep, a forge recipe, or colour. */
export type SaberKind = 'trace' | 'engine' | 'rite' | 'recipe' | 'custom';

/** The saberes in the game (docs/SABERES.md §4). The draw only ever offers these. */
export type SaberId =
  | 'market' | 'boulder' | 'kennel' | 'crane' | 'sieve'
  | 'whistler' | 'lime' | 'drowse'
  | 'lullaby' | 'alarm' | 'props'
  | 'masonHammer' | 'mossHood' | 'absentCloak'
  | 'cat' | 'ensign' | 'song' | 'namestone';
export const SABER_IDS: SaberId[] = ['market', 'boulder', 'kennel', 'crane', 'sieve', 'whistler', 'lime', 'drowse', 'lullaby', 'alarm', 'props', 'masonHammer', 'mossHood', 'absentCloak', 'cat', 'ensign', 'song', 'namestone'];

/** Where a saber can turn up: the hard places with a chest (§5.1). */
export type SaberSource = 'crypt1' | 'crypt2' | 'crypt3' | 'lair' | 'cellars' | 'furrows' | 'sima' | 'saga' | 'boss' | 'unsleeping';
export const SABER_SOURCES: SaberSource[] = ['crypt1', 'crypt2', 'crypt3', 'lair', 'cellars', 'furrows', 'sima', 'saga', 'boss', 'unsleeping'];

export interface SaberDef {
  kind: SaberKind;
  /** The bastion's era it can be found from (it is of no use before). */
  era: number;
  /** Its place (§5.1): where it turns up most. Absent: everywhere alike (the cat). */
  place?: SaberSource;
}

export const SABERES: Record<SaberId, SaberDef> = {
  market: { kind: 'trace', era: 1, place: 'saga' },
  boulder: { kind: 'trace', era: 1, place: 'furrows' },
  kennel: { kind: 'trace', era: 1, place: 'lair' },
  crane: { kind: 'trace', era: 1, place: 'cellars' },
  sieve: { kind: 'trace', era: 1, place: 'crypt2' },
  whistler: { kind: 'engine', era: 1, place: 'crypt1' },
  lime: { kind: 'engine', era: 2, place: 'crypt3' },
  drowse: { kind: 'engine', era: 1, place: 'sima' },
  lullaby: { kind: 'rite', era: 1, place: 'crypt3' },
  alarm: { kind: 'rite', era: 1, place: 'lair' },
  props: { kind: 'rite', era: 1, place: 'boss' },
  masonHammer: { kind: 'recipe', era: 1, place: 'crypt1' },
  mossHood: { kind: 'recipe', era: 1, place: 'sima' },
  absentCloak: { kind: 'recipe', era: 1, place: 'unsleeping' },
  cat: { kind: 'custom', era: 1 },
  ensign: { kind: 'custom', era: 1, place: 'boss' },
  song: { kind: 'custom', era: 1, place: 'furrows' },
  namestone: { kind: 'custom', era: 1, place: 'saga' },
};

export const SABER = {
  /**
   * Nothing is drawn before this instant (2026-11-07 00:00 UTC): the pieces a saber opens go out first, so every
   * campaign is on code that can draw and fight them before one is found (docs/SABERES.md §7, docs/DEPLOY.md).
   */
  from: 1_794_009_600_000,
  /** The group's level they open at (§5.2): the trail doesn't grow before. */
  openGroup: 4,
  /**
   * Trail each source adds, once a day for the bastion (the weekly ones once a week). The deep crypt adds its
   * per-floor number for each floor reached; the Chasm's weekly chest the higher one from mark `simaHighMark`.
   */
  trail: { crypt1: 1, crypt2: 2, crypt3: 1, lair: 1, cellars: 3, furrows: 4, sima: 3, saga: 4, boss: 4, unsleeping: 6 } as Record<SaberSource, number>,
  weekly: ['furrows', 'sima'] as SaberSource[],
  simaMark: 10,
  simaHighMark: 20,
  simaHigh: 5,
  /** The chance at each source: trail × this (bp), up to `chanceCapBp`. */
  chancePerTrailBp: 4,
  chanceCapBp: 100,
  /**
   * With the trail at the floor a saber is sure: 55 for the first, 60 for the second, then 60 more each. Set on
   * 2026-11-04 with `pnpm balance:saberes` on the most active real group: the first on day 8, then every 13–17 days.
   */
  floors: [55, 60] as number[],
  floorStep: 60,
  /** A saber weighs this much at its place and 1 anywhere else. */
  placeWeight: 8,
  /** A custom found halves the trail instead of clearing it (a cat shouldn't cost a whole round). */
  customKeepsBp: 5000,
  /** The draw's own salt (nested: `deriveSeed(deriveSeed(seed, salt), draws)`), clear of every other. */
  salt: 0x5ab3e5,
};

/** The trail floor after `found` saberes. */
export const saberFloor = (found: number): number =>
  found < SABER.floors.length ? (SABER.floors[found] as number) : (SABER.floors[SABER.floors.length - 1] as number) + SABER.floorStep * (found - SABER.floors.length + 1);

/** The structures only a saber opens: they show nowhere until the bastion knows it (§2, rule 4). */
export const SABER_STRUCTURES: Partial<Record<StructureType, SaberId>> = { market: 'market', boulder: 'boulder', kennel: 'kennel', crane: 'crane', sieve: 'sieve' };

// --- The market of Musgoviejo (§4.1) ---

/** Once a day: three of a resource for one of another, two for one when Odna trusts the garrison. */
export const MARKET = {
  rate: 3,
  trustedRate: 2,
  /** Odna's affinity (saga memory) from which the village trades two for one. */
  trustFrom: 2,
  /** At most this much comes in a day. */
  maxPerDay: 300,
};

// --- The whistling ballista (§4.2) ---

/** Its damage is its branch's (`BRANCHES.whistler`, 60 % of a ballista's). */
export const WHISTLER = {
  /** A nightmare it hits runs away this long, then can't be put to flight again for `calmTicks`. */
  fleeTicks: 18,
  calmTicks: 48,
};

// --- Rites at the keep (§4.3) ---

export type RiteId = 'lullaby' | 'alarm' | 'props';
export const RITE_IDS: RiteId[] = ['lullaby', 'alarm', 'props'];
export const RITES: Record<RiteId, { budgetBp: number; strengthBp: number }> = {
  /** Fewer, tougher: 0.8 of the budget, each nightmare (not the boss) with 1.32 of life and damage: 1.056 of each in all. */
  lullaby: { budgetBp: 8000, strengthBp: 13200 },
  /** The alarm: 15 % more budget all the Marea; ALARM says what it pays. */
  alarm: { budgetBp: 11500, strengthBp: 10000 },
  /** The props: the waves as they are; PROPS says what the gates do. */
  props: { budgetBp: 10000, strengthBp: 10000 },
};
/** The alarm's bet: every wave of the Marea won pays this share of what the rite cost; not in a boss's Marea. */
export const ALARM = { payBp: 16000 };
/**
 * The props: gates take this share of every blow (the dream mortar doesn't count on them), cost a wall run's to
 * walk through (their life over 4, not 8), and aren't repaired between the Marea's waves.
 */
export const PROPS = { gateTakenBp: 5000, pathDivisor: 4 };

// --- Forge recipes (§4.4) ---

/** Which saber each recipe base is (docs/SABERES.md §4.4). */
export const RECIPE_SABER: Partial<Record<GearBaseId, SaberId>> = { masonHammer: 'masonHammer', mossHood: 'mossHood', absentCloak: 'absentCloak' };

/** The absent's cloak: what its spectre fights in the bastion's waves gives this much more experience (bp). */
export const ABSENT_CLOAK = { ghostXpBp: 15000 };

export const RECIPES = {
  /** A recipe piece comes uncommon, at the hero's power; one per hero and Marea. Its dust: the reforge of an uncommon × this. */
  dustX: 3,
};

/** The mason's hammer: a basic blow on a nightmare next to a hurt structure of the group mends it. */
/** The mason's hammer: its −8 % blow and its 30 % mend are its base's trait (GEAR_BASES.masonHammer). */
export const MASON_HAMMER = {
  /** The life it gives back to one structure in a fight, at most (bp of its life). */
  capBp: 1000,
  /** How close the structure has to be to the nightmare struck (fixed units: one cell). */
  reach: 1000,
};

// --- The garrison's cat (§4.5) ---

/** One cat, and a kitten more with each era past the first, up to `kittens`. */
export const CAT = { kittens: 3, names: 12 };

// --- Stage 2 and 3 pieces (§4.1, §4.2, §4.5) ---

/** The boulder: a trap with one charge, rolling downhill up to `cells` cells, a blow for each nightmare on foot crossed. */
export const BOULDER = { cells: 6 };

/** The kennel: mastiffs let out a while into each wave, that hunt the ones going the long way round. */
export const KENNEL = {
  /** Dogs by the kennel's level (1–3). */
  dogs: [2, 2, 3] as number[],
  /** Ticks into the wave they come out. */
  at: 120,
  hp: 260,
  damage: 9,
  every: 14,
  radius: 650,
  /** A bitten one is held (can't move) this long, then not again for `pinEvery` (half for warlords, none for the big). */
  pin: 12,
  pinEvery: 36,
  /** Fixed units a tick, and how often each dog looks for its prey again. */
  step: 115,
  retarget: 12,
  /** How far from the keep a dog looks for its prey. */
  hunt: 16000,
  /** What a bitten one gives back (bp of its blow, each bite): dogs fall, and a long wave empties the kennel. */
  biteBackBp: 10000,
};

/**
 * What the general reckons the saberes that fight without shooting are worth on a cell (`coverAt`, docs/OBRAS.md §2):
 * fire per second as a tower's, within `range` of them (a kennel's dogs roam, a charged boulder guards its slope).
 */
export const SABER_COVER = { kennel: { range: 6000, fire: 18 }, boulder: { range: 2000, fire: 30 } };

/** The crane: golems' work on the jobs this close (cells) goes this much faster (bp); one golem stays at it. */
export const CRANE = { reach: 4, workBp: 15000 };

/** The sieve: nightmares falling this close (fixed units) leave their dust; one a nightmare, up to `cap` a wave, sifted by a golem for `siftMs`. */
export const SIEVE = { reach: 3000, perKill: 1, cap: 100, siftMs: 4 * 3_600_000 };

/** The lime catapult: its cloud's radius and how long it lasts (ticks); a shooter inside can't shoot. Its damage is its branch's. */
export const LIME = { radius: 1000, ticks: 48, every: 12 };

/** The drowsing tower: one shot in `every` puts its target to sleep this long instead of hurting it. */
export const DROWSE = { every: 3, ticks: 48 };

/** The group's ensign: a field, a colour and a sign, from these; changed once an era. */
/** The colours' paint is the render's (ENSIGN_COLORS, engine/ensign.ts): dragon red, charcoal, vigil blue, moss green, slate, ash. */
export const ENSIGN = { fields: 4, colors: 6, signs: 8 };

/** The stone of names: a line for each Marea won since it was found, up to this many kept. */
export const NAMESTONE = { lines: 80 };
